import React from "react";
import { useSelector } from "react-redux";

import { useInput } from "../../hooks/use-input";
import { useThunk } from "../../hooks/useThunk";
import {
  validateCouponCode,
  validateInfluencer,
  validateDiscountType,
  validatePlanType,
  validateDiscountAmount
} from "../../utils/validationUtils";
import showToast from "../../utils/toastNotifications";
import { createCoupon } from "../../services/thunks/couponThunks";

import CreateOneTimeCoupon from "./CreateOneTimeCoupon";
import CreateUnlimitedCoupon from "./CreateUnlimitedCoupon";

const discountOptions = [
  { _id: "10% Off", name: "10% Off" },
  { _id: "25% Off", name: "25% Off" },
  { _id: "50% Off", name: "50% Off" },
  { _id: "Free", name: "Free" },
];

const planOptions = [
  { _id: "Monthly", name: "Monthly" },
  { _id: "Annually", name: "Annually" },
];

const CreateCoupon = ({
  couponType = "onetime",
  isFixedAmount = false,
  setHighlightedRowId,
  setRefreshTrigger,
  pageSetterRef,
}) => {
  const auth = useSelector((state) => state.auth);
  const [doCreateCoupon, isCreatingCoupon] = useThunk(createCoupon);

  const {
    value: couponCode,
    handleInputChange: handleCouponCodeChange,
    handleInputBlur: handleCouponCodeBlur,
    hasError: couponCodeHasError,
    errorMessage: couponCodeErrorMessage,
    setErrorMessage,
    reset: resetCouponCode,
  } = useInput("", validateCouponCode);

  const {
    value: discountType,
    handleInputChange: handleDiscountTypeChange,
    handleInputBlur: handleDiscountTypeBlur,
    errorMessage: discountTypeErrorMessage,
    reset: resetDiscountType,
  } = useInput("", validateDiscountType);

  const {
    value: planType,
    handleInputChange: handlePlanTypeChange,
    handleInputBlur: handlePlanTypeBlur,
    errorMessage: planTypeErrorMessage,
    reset: resetPlanType,
  } = useInput("", validatePlanType);

  const {
    value: influencerName,
    handleInputChange: handleInfluencerChange,
    handleInputBlur: handleInfluencerBlur,
    errorMessage: influencerErrorMessage,
    hasError: influencerHasError,
    reset: resetInfluencer,
  } = useInput("", validateInfluencer);

  const {
    value: discountAmount,
    handleInputChange: handleDiscountAmountChange,
    handleInputBlur: handleDiscountAmountBlur,
    errorMessage: discountAmountErrorMessage,
    hasError: discountAmountHasError,
    reset: resetDiscountAmount,
  } = useInput("", validateDiscountAmount);

  const handleSubmit = async () => {
    if (!isValidForm) return;

    const payload = {
      code: couponCode.trim().toUpperCase(),
      planType,
    };

    if (isFixedAmount) {
      payload.discountAmount = parseFloat(discountAmount);
      payload.isFixedAmount = true;
    } else {
      payload.discountType = discountType;
    }

    if (couponType === "unlimited") {
      payload.influencer = influencerName.trim();
    }

    try {
      const result = await doCreateCoupon({
        token: auth.token,
        payload,
      });

      if (result.success) {
        const newId = result.response?.data?.coupon?._id || result.id;
        const newIndex = result.response?.data?.index;
        showToast("success", `Coupon ‘${couponCode}’ added successfully.`);
        resetCouponCode();
        resetDiscountType();
        resetPlanType();
        resetInfluencer();
        resetDiscountAmount();

        const pageSize = 10;
        const pageNumber = Math.floor(newIndex / pageSize) + 1;
        if (pageSetterRef.current) pageSetterRef.current(pageNumber);

        setHighlightedRowId(newId);
        setTimeout(() => setHighlightedRowId(null), 5000);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        setErrorMessage(result.error.message);
      }
    } catch (error) {
      showToast("error", error.message || "An error occurred");
    }
  };

  const isValidForm = (() => {
    const baseValidation = couponCode && !couponCodeHasError && planType;

    const discountValidation = isFixedAmount
      ? discountAmount && !discountAmountHasError
      : discountType;

    const influencerValidation = couponType === "onetime" || (influencerName && !influencerHasError);

    return baseValidation && discountValidation && influencerValidation;
  })();

  const commonProps = {
    couponCode,
    couponCodeErrorMessage,
    handleCouponCodeChange,
    handleCouponCodeBlur,
    planType,
    planTypeErrorMessage,
    handlePlanTypeChange,
    handlePlanTypeBlur,
    planOptions,
    handleSubmit,
    isValidForm,
    isCreatingCoupon,
    isFixedAmount,
  };

  // Props for percentage-based discounts
  const percentageProps = !isFixedAmount ? {
    discountType,
    discountTypeErrorMessage,
    handleDiscountTypeChange,
    handleDiscountTypeBlur,
    discountOptions,
  } : {};

  // Props for fixed amount discounts
  const fixedAmountProps = isFixedAmount ? {
    discountAmount,
    discountAmountErrorMessage,
    handleDiscountAmountChange,
    handleDiscountAmountBlur,
  } : {};

  // Props for unlimited coupons
  const unlimitedProps = couponType === "unlimited" ? {
    influencerName,
    influencerErrorMessage,
    handleInfluencerChange,
    handleInfluencerBlur,
    influencerHasError,
  } : {};

  return (
    <div className="bg-[#111111D6] border border-[#505050] p-6 rounded-lg mb-8">
      <h2 className="text-xl font-semibold mb-4 text-[#E3E3E3]">
        Add New Coupon {isFixedAmount ? "(Fixed Amount)" : "(Percentage)"}
      </h2>

      {couponType === "unlimited" ? (
        <CreateUnlimitedCoupon
          {...{
            ...commonProps,
            ...percentageProps,
            ...fixedAmountProps,
            ...unlimitedProps,
          }}
        />
      ) : (
        <CreateOneTimeCoupon
          {...{
            ...commonProps,
            ...percentageProps,
            ...fixedAmountProps,
          }}
        />
      )}

    </div>
  );
};

export default CreateCoupon;
